import { PREFIXO_DA_LEI, type ModuleKey } from '@titularis/ui';
import capaEssay from './assets/capas/essay.webp';
import capaLegislacao from './assets/capas/legislacao.webp';
import capaObjective from './assets/capas/objective.webp';

/**
 * O que se vende, com cara.
 *
 * As telas de plano e de assinatura mostravam o produto como uma linha de
 * texto com um ícone verde ao lado - o mesmo ícone para os quatro, porque o
 * ícone não dizia qual produto era, dizia "ativo". Produto sem rosto não se
 * distingue do vizinho e não parece valer novecentos reais.
 *
 * A arte já existia: é a mesma capa que a Home usa na vitrine de módulos. O
 * que faltava era esta ponte, entre a chave do produto no servidor
 * (`entitlements.product`, texto livre por decisão comercial) e a capa no
 * bundle. Ela mora aqui, e não em `grandesModulos.ts`, porque as duas listas
 * respondem perguntas diferentes: lá é **o que se estuda**; aqui é **o que se
 * compra**.
 *
 * **A Legislação Comentada saiu daqui em 07/09/2026, e o motivo é a
 * produtização** (D-44/D-45): deixou de ser um produto com nome fixo e virou uma
 * chave por lei, cujo nome só existe depois de uma ida ao acervo. Um mapa
 * escrito no bundle não tem como saber que `commented_law:<uuid>` é a Lei de
 * Registros Públicos - quem sabe é o servidor, e é ele que manda o rótulo.
 *
 * Produto que o servidor conheça e esta lista não vira uma linha sem capa, com
 * a própria chave como nome - feio, mas nunca em branco.
 */
export interface Produto {
  /** Rótulo curto, o mesmo da vitrine. */
  nome: string;
  /** Uma linha sobre o que a compra entrega, para quem ainda não comprou. */
  resumo: string;
  capa: string;
  /**
   * A cor do produto, para o acento da tela.
   *
   * Repete `moduleAccent` do tema em vez de importá-lo porque nem todo produto
   * é fase de prova: a Legislação Comentada tem cor e não tem `ModuleKey`.
   */
  acento: string;
  /** A fase correspondente, quando o produto é uma. */
  moduleKey?: ModuleKey;
}

export const PRODUTOS: Record<string, Produto> = {
  enac: {
    nome: 'Enac',
    resumo: 'O Exame Nacional de Cartórios, edição a edição, com resolução comentada.',
    /*
     * Pela pasta pública, e não pelo bundle: o e-mail da compra mostra a mesma
     * capa e precisa de uma URL estável, e o Vite carimba um hash no nome de
     * tudo o que é importado. É a regra para capa de produto vendável - ver
     * assets/capas/README.md.
     */
    capa: '/capas/enac.png',
    acento: '#1f6f6b',
    moduleKey: 'enac',
  },
  objective: {
    nome: '1ª fase',
    resumo: 'As objetivas dos concursos estaduais, filtradas por banca, disciplina e assunto.',
    capa: capaObjective,
    acento: '#2f5aa8',
    moduleKey: 'objective',
  },
  essay: {
    nome: '2ª fase',
    resumo: 'Dissertação, peça prática e discursivas, com espelho e resolução.',
    capa: capaEssay,
    acento: '#8a4a2b',
    moduleKey: 'essay',
  },
};

/*
 * A separação das leis **subiu para o `@titularis/ui`** em 11/09/2026, quando a
 * landing pública precisou da mesma regra: lá o Enac aparecia com as sete leis
 * enumeradas, e o cartão do plano gratuito ao lado ficava oco. Segue reexportada
 * daqui para as telas do app continuarem pedindo ao vizinho.
 */
export { ehLei, separarLeis } from '@titularis/ui';

/**
 * O produto, ou um substituto honesto quando o servidor conhece um que não
 * conhecemos.
 *
 * **A Legislação Comentada tem um rosto só, e um nome que vem de fora.** Não há
 * como pôr cada lei neste mapa - elas nascem quando alguém importa uma lei -,
 * mas todas compartilham a mesma capa e o mesmo acento, e o nome chega pronto do
 * servidor (`rotulo`). Sem isto, quem comprasse o Provimento 149 veria
 * `commented_law:9f2c...` na tela de "Seus produtos".
 */
export function produto(chave: string, rotulo?: string | null): Produto | undefined {
  const conhecido = PRODUTOS[chave];
  if (conhecido) return conhecido;

  if (chave.startsWith(PREFIXO_DA_LEI)) {
    return {
      nome: rotulo ?? 'Legislação Comentada',
      resumo: 'Esta lei, comentada artigo a artigo.',
      capa: capaLegislacao,
      acento: '#6b4a8a',
    };
  }

  return undefined;
}

/** O nome do produto para a interface; o rótulo do servidor, ou a chave crua. */
export function nomeDoProduto(chave: string, rotulo?: string | null): string {
  return PRODUTOS[chave]?.nome ?? rotulo ?? chave;
}
